import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getSubsectionHeadingSizeClass } from '../../utils/fontSizeUtils';

interface ShareListModalProps {
  isOpen: boolean;
  onClose: () => void;
  listId: number | null | undefined;
  currentList: UserTickerList | null;
  onUpdate: () => void;
  isListLocked?: boolean; // If true, list is locked (beyond free tier limit) and sharing is disabled
}

const ShareListModal: React.FC<ShareListModalProps> = ({
  isOpen,
  onClose,
  listId,
  currentList,
  onUpdate,
  isListLocked = false,
}) => {
  const location = useLocation();
  const { fontSize } = useFontSize();
  const [shareEnabled, setShareEnabled] = useState(false);
  const [publicListName, setPublicListName] = useState('');
  const [publicUuid, setPublicUuid] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize state from currentList only when modal opens
  useEffect(() => {
    if (isOpen && currentList && !hasInitialized) {
      // If list is locked, automatically disable sharing
      const shouldBeEnabled = isListLocked ? false : (currentList.share_enabled ?? false);
      setShareEnabled(shouldBeEnabled);
      setPublicListName(currentList.public_list_name || '');
      setPublicUuid(currentList.public_uuid || null);
      setHasInitialized(true);
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      setHasInitialized(false);
    }
  }, [isOpen, currentList, hasInitialized, isListLocked]);

  // Automatically disable sharing when list becomes locked
  useEffect(() => {
    if (isOpen && isListLocked && shareEnabled && listId && currentList) {
      // If list is locked and sharing is enabled, disable it automatically
      handleToggleChange(false).catch(err => {
        console.error('Failed to disable sharing for locked list:', err);
      });
    }
  }, [isOpen, isListLocked, shareEnabled, listId, currentList]);

  // Get the current list page path segment for shared links (must match App routes)
  const getCurrentPagePath = () => {
    const path = location.pathname;
    if (path === '/' || path === '/daily') return 'daily';
    if (path === '/performance') return 'performance';
    if (path === '/technicals') return 'technicals';
    if (path === '/fundamentals') return 'fundamentals';
    if (path === '/proscons') return 'proscons';
    if (path === '/analysts') return 'analysts';
    if (path === '/insiders') return 'insiders';
    return 'daily'; // default
  };

  // Generate public URL
  const getPublicUrl = () => {
    if (!publicUuid) return '';
    const pagePath = getCurrentPagePath();
    const baseUrl = window.location.origin;
    return `${baseUrl}/${pagePath}?public_uuid=${publicUuid}`;
  };

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    const url = getPublicUrl();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setError('Failed to copy URL to clipboard');
    }
  };

  // Handle toggle change - update immediately
  const handleToggleChange = async (enabled: boolean) => {
    if (!listId || !currentList) return;

    setIsUpdating(true);
    setError(null);

    try {
      // Build update object
      const updateData: {
        list_name: string;
        public_list_name?: string;
        share_enabled: boolean;
      } = {
        list_name: currentList.list_name,
        share_enabled: enabled,
      };

      // Only include public_list_name if sharing is enabled (to preserve it when disabled)
      if (enabled) {
        updateData.public_list_name = publicListName.trim() || '';
      }
      // When disabling, don't include public_list_name so it remains unchanged

      // Get the updated list data from the API response
      const updatedList = await userTickerListApi.updateList(listId, updateData);

      // Update local state immediately with the response data
      setPublicUuid(updatedList.public_uuid || null);
      setPublicListName(updatedList.public_list_name || '');
      setShareEnabled(updatedList.share_enabled ?? false);

      // Refresh the parent component's data in the background
      onUpdate();
    } catch (err: any) {
      console.error('Failed to update list sharing settings:', err);
      setError(err.message || 'Failed to update sharing settings');
      // Revert toggle on error
      setShareEnabled(!enabled);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle public list name update
  const handleUpdatePublicName = async () => {
    if (!listId || !currentList) return;

    // Frontend validation: ensure public list name is not empty
    const trimmedName = publicListName.trim();
    if (!trimmedName) {
      setError('Public list name cannot be empty');
      return;
    }

    setIsUpdatingName(true);
    setError(null);

    try {
      // Build update object - only update public_list_name
      const updateData: {
        list_name: string;
        public_list_name: string;
        share_enabled?: boolean;
      } = {
        list_name: currentList.list_name,
        public_list_name: trimmedName,
        share_enabled: shareEnabled,
      };

      // Get the updated list data from the API response
      const updatedList = await userTickerListApi.updateList(listId, updateData);

      // Update local state immediately with the response data
      setPublicListName(updatedList.public_list_name || '');

      // Refresh the parent component's data in the background
      onUpdate();
    } catch (err: any) {
      console.error('Failed to update public list name:', err);
      setError(err.message || 'Failed to update public list name');
    } finally {
      setIsUpdatingName(false);
    }
  };

  if (!isOpen) return null;

  const publicUrl = getPublicUrl();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 dark:bg-gray-900/70">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md m-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-gray-900 dark:text-white`}>
              Share Your List
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              type="button"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded-md">
              <p className={`${getTextSizeClass(fontSize)} text-red-700 dark:text-red-300`}>{error}</p>
            </div>
          )}

          {/* Share Toggle */}
          <div className="mb-6">
            <label className="flex items-center justify-between">
              <span className={`${getTextSizeClass(fontSize)} font-medium text-gray-700 dark:text-gray-300`}>
                Enable List Sharing
              </span>
              {isListLocked && (
                <span className={`${getTextSizeClass(fontSize)} text-gray-500 dark:text-gray-400 text-sm mr-2`}>
                  (Locked lists cannot be shared)
                </span>
              )}
              <button
                onClick={() => !isUpdating && !isListLocked && handleToggleChange(!shareEnabled)}
                disabled={isUpdating || isListLocked}
                className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  shareEnabled
                    ? 'bg-primary'
                    : 'bg-gray-200 dark:bg-gray-700'
                } ${isUpdating || isListLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={shareEnabled}
                aria-label="Toggle list sharing"
                aria-disabled={isListLocked}
              >
                <span className="sr-only">Toggle list sharing</span>
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
                    shareEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Public List Name Input (shown when sharing is enabled) */}
          {shareEnabled && (
            <div className="mb-6">
              <label
                htmlFor="public-list-name"
                className={`block ${getTextSizeClass(fontSize)} font-medium text-gray-700 dark:text-gray-300 mb-2`}
              >
                List Name Shown to Others
              </label>
              <div className="flex items-center space-x-2">
                <input
                  id="public-list-name"
                  type="text"
                  value={publicListName}
                  onChange={(e) => setPublicListName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdatePublicName();
                    }
                  }}
                  placeholder="Enter a name for your public list"
                  maxLength={100}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleUpdatePublicName}
                  disabled={isUpdatingName || !listId}
                  className={`bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 ${getTextSizeClass(fontSize)} disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-w-[80px] sm:min-w-[100px]`}
                  type="button"
                >
                  Update
                </button>
              </div>
            </div>
          )}

          {/* Public URL (shown when sharing is enabled) */}
          {shareEnabled && (
            <div className="mb-6">
              <label className={`block ${getTextSizeClass(fontSize)} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
                Link to Your List
              </label>
              {publicUuid ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={publicUrl}
                    readOnly
                    className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-300 ${getTextSizeClass(fontSize)} font-mono`}
                  />
                  <button
                    onClick={handleCopyUrl}
                    className={`bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 ${getTextSizeClass(fontSize)} whitespace-nowrap min-w-[80px] sm:min-w-[100px]`}
                    type="button"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <p className={`${getTextSizeClass(fontSize)} text-blue-700 dark:text-blue-300`}>
                    A public URL will be generated after sharing is enabled.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareListModal;

